import React from "react";
import classNames from "classnames";
import Checkbox from "components/form/checkbox/Checkbox";
import { resolveEmailBuilderSettingsPanelScrollRoot } from "../emailBuilderSettingsPanelScroll";

export type EmailBuilderSettingsRadioOption<T extends string> = {
  value: T;
  label: string;
};

type EmailBuilderSettingsRadioGroupProps<T extends string> = {
  name: string;
  value: T;
  options: readonly EmailBuilderSettingsRadioOption<T>[];
  onChange: (value: T) => void;
  ariaLabel: string;
  className?: string;
};

export function EmailBuilderSettingsRadioGroup<T extends string>(
  props: EmailBuilderSettingsRadioGroupProps<T>,
) {
  const { name, value, options, onChange, ariaLabel, className } = props;

  function selectOption(optionValue: T, label: HTMLElement): void {
    const input = label.querySelector("input");
    const scrollRoot = resolveEmailBuilderSettingsPanelScrollRoot(
      input instanceof HTMLInputElement ? input : label,
    );
    const savedScrollTop = scrollRoot?.scrollTop ?? null;

    if (optionValue !== value) {
      onChange(optionValue);
    }

    if (input instanceof HTMLInputElement) {
      input.focus({ preventScroll: true });
    }

    if (scrollRoot && savedScrollTop !== null) {
      scrollRoot.scrollTop = savedScrollTop;
    }
  }

  return (
    <div
      className={classNames("email-builder-settings-radio-group", className)}
      role="radiogroup"
      aria-label={ariaLabel}
    >
      {options.map(option => {
        const selected = option.value === value;
        return (
          <label
            key={option.value}
            className="email-builder-settings-radio-group__option"
            onMouseDown={event => {
              event.preventDefault();
              selectOption(option.value, event.currentTarget);
            }}
          >
            <Checkbox
              circle
              size="small"
              name={name}
              value={option.value}
              checked={selected}
              readOnly
              labelHidden
            />
            <span className="email-builder-settings-radio-group__label">{option.label}</span>
          </label>
        );
      })}
    </div>
  );
}
